import gql from "graphql-tag";
import {useCallback} from "react";

import {useMutation} from "@repo/data-gql";
import {gqlOp, type GqlOperation} from "@repo/data-gql-core";
import {Errors, KAError} from "@repo/logging";

import type {SubmitActivityAssignmentMutationType} from "./__graphql-types__/index.ts";
import type {SubmitActivityAssignmentMutation_submitActivityAssignment_notification as SubmitActivityAssignmentNotification} from "./__graphql-types__/SubmitActivityAssignmentMutation.ts";

export const SUBMIT_ACTIVTY_ASSIGNMENT_MUTATION: GqlOperation<
    SubmitActivityAssignmentMutationType["response"],
    SubmitActivityAssignmentMutationType["variables"]
> = gqlOp(gql`
    mutation SubmitActivityAssignmentMutation(
        $assignmentID: ID!
        $threadID: ID!
    ) {
        submitActivityAssignment(
            assignmentID: $assignmentID
            threadID: $threadID
        ) {
            notification {
                classroomName
                firstCompletion
            }
            error {
                code
            }
        }
    }
`);

export const useSubmitAssignmentMutation = (): ((
    assignmentID: string,
    threadID: string,
) => Promise<SubmitActivityAssignmentNotification | null | undefined>) => {
    const [mutate] = useMutation(SUBMIT_ACTIVTY_ASSIGNMENT_MUTATION);
    const submitAssignmentMutation = useCallback(
        async (assignmentID: string, threadID: string) => {
            const result = await mutate({assignmentID, threadID});
            // If we have an error status or we got a successful response but
            // it has an error in it, then we throw an error.
            // We should never see the loading status here since this waits
            // for the mutation to be complete.
            const error =
                (result.status === "error"
                    ? result.error
                    : result.status === "success" &&
                        result.data.submitActivityAssignment?.error
                      ? result.data.submitActivityAssignment.error.code
                      : null) ||
                (result.status === "aborted" ? "aborted" : null);
            if (error) {
                throw new KAError(
                    "Failed to submit assignment mutation",
                    Errors.KhanService,
                    {cause: error},
                );
            }

            if (result.status !== "success") {
                return null;
            }

            return result.data.submitActivityAssignment?.notification;
        },
        [mutate],
    );

    return submitAssignmentMutation;
};
